import { IssueStatusType } from '@kcftech/prometheus-client';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { isNodeInactive } from 'utils/node';
import {
    getIssueMode,
    IssueMode,
    isHardwareHealthIssue,
    isMachineHealthIssue,
    type PlainIssue,
} from '../shared';

export type IssueDetailsContext = Readonly<{
    issue: PlainIssue;
    groupUnlinked: boolean;
    hardwareLocationMoved: boolean | undefined;
    nodeInactive: boolean | undefined;
    isInternal: boolean | undefined;
    isDraft: boolean;
    issueType: IssueMode;
}>;

function buildIssueDetailsContext(issue: PlainIssue): IssueDetailsContext {
    return {
        issue,
        groupUnlinked: isMachineHealthIssue(issue) && issue.Group === undefined,
        hardwareLocationMoved:
            isHardwareHealthIssue(issue) && issue.HardwareLocationMoved,
        nodeInactive:
            isHardwareHealthIssue(issue) &&
            issue.Node &&
            isNodeInactive(issue.Node.NodeStatusCode),
        isInternal: isHardwareHealthIssue(issue) && issue.IsInternal,
        isDraft: issue.IssueStatus.StatusType === IssueStatusType.Draft,
        issueType: getIssueMode(issue),
    };
}

const Context = createContext<IssueDetailsContext | undefined>(undefined);

export type IssueDetailsProviderProps = Readonly<{
    issue: PlainIssue;
    children?: ReactNode;
}>;

export function IssueDetailsProvider({ issue, children }: IssueDetailsProviderProps) {
    const value: IssueDetailsContext = useMemo(
        () => buildIssueDetailsContext(issue),
        [issue],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useIssueDetailsContext(): IssueDetailsContext {
    const ctx = useContext(Context);

    if (!ctx) {
        throw new Error('Missing <IssueDetailsProvider>');
    }

    return ctx;
}
